import type { GeoJSONFeature, Map as MapboxMap, Point } from 'mapbox-gl';
import { LayerId } from '../constants';

/**
 * A padded hit box: the dot spills below the coordinate while the ring ends on it, so an
 * exact-pixel query would oscillate expand/collapse in that band. Shared with the zone click so
 * both sides agree on where a deal is — a mark that answers the hover must also outrank the zone
 * underneath it, or a click just off the dot opens the deal and selects the zone at once.
 */
export const DEAL_HIT_PADDING_PX = 8;

/**
 * The deal mark under a point, expanded or resting. Expanding swaps a deal from the dot layer to
 * the ring layer, so a query against one layer loses the mark the moment it unfolds — both are
 * asked, filtered to the ones the style currently holds because queryRenderedFeatures answers []
 * for the whole call when any requested layer is absent.
 */
export function dealFeatureAt(map: MapboxMap, point: Point): GeoJSONFeature | undefined {
  // The unfolded pins first: they are drawn above the clustered pair, so a pin over a cluster must
  // win the hit test the way it wins the paint.
  const layers = [
    LayerId.SELECTED_DEAL_RING,
    LayerId.SELECTED_DEAL_POINT,
    LayerId.DEALS_POINT,
    LayerId.DEALS_RING,
  ].filter((id) => map.getLayer(id));
  if (layers.length === 0) return undefined;

  const { x, y } = point;
  const hitBox: [[number, number], [number, number]] = [
    [x - DEAL_HIT_PADDING_PX, y - DEAL_HIT_PADDING_PX],
    [x + DEAL_HIT_PADDING_PX, y + DEAL_HIT_PADDING_PX],
  ];
  return map.queryRenderedFeatures(hitBox, { layers })[0];
}
